import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { Button, Grid, Spinner } from 'gu-ui-kit'
import styles from 'modules/Modals/Confirmation/confirmationmodal.module.scss'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import clsx from 'clsx'
import {
  INITIALIZE,
  SUBMIT,
} from 'modules/Services/CampaignsEditor/subcomponents/BudgetEditor/types'
import EditModeSwitcher from 'modules/Services/CampaignsEditor/subcomponents/BudgetEditor/subcomponents/EditorTypeSwitcher'
import ControlsPanel from 'modules/Services/CampaignsEditor/subcomponents/BudgetEditor/subcomponents/ControlsPanel'
import ModifyList from 'modules/Services/CampaignsEditor/subcomponents/BudgetEditor/subcomponents/ModifyList'
import { notificationShow } from 'modules/Notifications/actions'

const AVAILABLE_EDITOR_TYPES = ['budget', 'dates']

const BudgetEditorModal = (props) => {
  const { t } = useTranslation()
  const { closeModal, meta, resource } = props
  const dispatch = useDispatch()
  const [type, setType] = useState(AVAILABLE_EDITOR_TYPES[0])
  const [submitting, setSubmitting] = useState(false)
  const [hasUnsubmittedData, setHasUnsubmittedData] = useState(true)
  const [successfullySubmitted, setSuccessfullySubmitted] = useState(false)

  const { budgetRows, datesRows } = useSelector(
    (state) => ({
      budgetRows: state['services/budget-editor'].budget.rows,
      datesRows: state['services/budget-editor'].dates.rows,
    }),
    shallowEqual,
  )
  const budgetRowsTouched = useMemo(
    () => budgetRows.filter((r) => r?.rowState?.touched === true),
    [budgetRows],
  )
  const datesRowsTouched = useMemo(
    () => datesRows.filter((r) => r?.rowState?.touched === true),
    [datesRows],
  )

  useEffect(() => {
    setHasUnsubmittedData(budgetRowsTouched.length > 0 || datesRowsTouched.length > 0)
  }, [budgetRowsTouched, datesRowsTouched])

  const apply = useCallback(async () => {
    setSubmitting(true)

    return await new Promise((resolve, reject) =>
      dispatch({
        type: SUBMIT,
        payload: { resource },
        meta: { resolve, reject },
      }),
    )
      .then(() => {
        dispatch(
          notificationShow({
            type: 'success',
            message: t('campaigns_editor::budget_editor.notifications.success_updated', {
              context: resource,
            }),
          }),
        )
        setSuccessfullySubmitted(true)
      })
      .catch(() => setSuccessfullySubmitted(false))
      .finally(() => setSubmitting(false))
  }, [setSubmitting, setSuccessfullySubmitted, resource])

  useEffect(() => dispatch({ type: INITIALIZE }), [])

  return (
    <>
      <EditModeSwitcher
        resource={resource}
        availableEditorTypes={AVAILABLE_EDITOR_TYPES}
        currentType={type}
        setType={setType}
      />
      <ControlsPanel
        type={type}
        resource={resource}
      />
      <ModifyList
        type={type}
        resource={resource}
      />
      <Grid
        gap={16}
        direction={'column-reverse'}
        fb={{
          direction: 'row',
        }}
        justifyContent={'between'}
        className={clsx(styles['confirmation-modal__button-container'], 'margin-top-32')}
      >
        <Button
          onClick={() => {
            meta.resolve({})
            closeModal()
          }}
          variant={'ghost'}
          grey
          disabled={submitting}
        >
          {t('common::confirmation_modal.cancel')}
        </Button>

        <Button
          startIcon={submitting ? <Spinner width={'16px'} /> : null}
          variant={'primary'}
          onClick={hasUnsubmittedData ? apply : closeModal}
          disabled={!successfullySubmitted && (!hasUnsubmittedData || submitting)}
        >
          {successfullySubmitted && !hasUnsubmittedData
            ? t('common::close_button')
            : t('common::buttons.save')}
        </Button>
      </Grid>
    </>
  )
}

BudgetEditorModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  buttons: PropTypes.object.isRequired,
  submitting: PropTypes.bool,
  meta: PropTypes.object,
  ids: PropTypes.array,
  resource: PropTypes.string.isRequired,
}

export default BudgetEditorModal
